import { z } from 'zod'

import { WorldlineOrder, WorldlineOrderStatus } from '@/travelsuit/worldline'
import { TripPaymentMethod } from '@/types/payment'

import { UserCurrency, WithCancellationStatus, WithUserId } from './common'
import { CreditCardResponse } from './creditCard'
import { ErrorsInBooking } from './errors'
import { FlightFareGroupKey, FlightOptionKey } from './flights/common'
import type { IncludedExtras, RailPassengerId } from './rails/common'
import { User } from './user'

export const BookingSegmentIdZ = z.number().brand<'BookingSegmentId'>()

export type BookingSegmentId = z.infer<typeof BookingSegmentIdZ>

export const BSTTIdZ = z.number().brand<'BSTTId'>()

export type BSTTId = z.infer<typeof BSTTIdZ>

// eslint-disable-next-line no-restricted-syntax
export enum TripDirection {
	OneWay = 'one_way',
	RoundTrip = 'round_trip',
	MultiDestination = 'multi_destination',
}

export const TripDirectionZ = z.enum(TripDirection)

// eslint-disable-next-line no-restricted-syntax
export enum VendorVerificationStatus {
	Active = 'active',
	Success = 'success',

	Unverified = 'unverified',
	InProgress = 'in-progress',

	PriceChanged = 'price_changed',
	Canceled = 'canceled',
	Failed = 'failed',
}

export interface BookingSegmentToTraveler extends WithCancellationStatus, WithUserId {
	id: BSTTId
	booking_segment_id: BookingSegmentId
	vendor_reservation_status: VendorVerificationStatus | null
	vendor_verification_url?: string
	booking_confirmation_number?: string
	actual_original_total: number | null
	actual_total: number

	fare_group_key: FlightFareGroupKey
	flight_option_keys: FlightOptionKey[]

	original_currency: string
	original_total: number | null

	currency: UserCurrency
	total_price: number
	total_tax: number

	payment_method_id: number | null
	payment_method: TripPaymentMethod | null

	cloned_booking_errors: ErrorsInBooking[] | null
	within_void_window: boolean | null
	has_unused_tickets: boolean | null

	status: ReservationStatus
	booking_errors: ErrorsInBooking[] | null

	pnr: string
	supplier_confirmation_number: string | null

	/** Rails only */
	passenger_id?: RailPassengerId
	reservation_details_response?: {
		fare_group: {
			traveler_price: {
				taxes: { original_amount: string; description: string }[]
			}
			validating_carrier_code?: string
		}
		sections?: {
			included_extras?: IncludedExtras
		}[]
	}

	rail_cards?: { card_id: string }[]
}

export type BookingSegmentToTravelerUpdates = Pick<
	BookingSegmentToTraveler,
	'id' | 'booking_segment_id' | 'booking_errors' | 'user_id' | 'status'
>

// eslint-disable-next-line no-restricted-syntax
export enum TripStatus {
	Draft = 'draft',
	DraftBySupport = 'draft_by_support',
	WaitingApproval = 'waiting_approval',
	Canceled = 'canceled',
	NotApproved = 'not_approved',
	Booked = 'booked',
	Booking = 'booking',
	Approved = 'approved',
	Reverted = 'reverted',
	PreTripGreenLight = 'pre_trip_green_light',
	SendingToApproval = 'sending_to_approval',
	PriceChanged = 'price_changed',
	WaitingForUserSubmission = 'waiting_for_user_submission',
	UrgentActionRequired = 'urgent_action_required',
}

export interface TripFeeStatusData {
	user: User
	total_price: number
	total_tax: number
	status: WorldlineOrderStatus
	order: WorldlineOrder
	credit_card: CreditCardResponse
}

export interface TripBookingStatusResponse {
	status: TripStatus
	errors: ErrorsInBooking[]
	trip_fees: TripFeeStatusData[]
	product_fees: TripFeeStatusData[]
	booking_segment_to_travelers: BookingSegmentToTravelerUpdates[]
}

// eslint-disable-next-line no-restricted-syntax
export enum ReservationStatus {
	Draft = 'draft',
	Submitted = 'submitted',
	Reverted = 'reverted',
	Booked = 'booked',
	BookFailed = 'book_failed',
	PreBooked = 'prebooked',
}

// eslint-disable-next-line no-restricted-syntax
export enum BookingSource {
	SubmitForApproval = 'sent_for_approval',
	ApproveOrReject = 'approve_or_reject',
	Book = 'book',
	BookBySupport = 'book_by_support',
}
